/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Issue, IssueType, IssueStatus, EstimateSize, parseIssuesFromMarkdown } from '@shared/utils/issues';
import { Bug, Plus, Search, FilterX, Loader, Download } from 'lucide-react';
import { SkillShopService } from '../services/skillShopService';
import { RequestInput } from '../components/common/RequestInput';
import { useRequestProcessor } from '../hooks/useRequestProcessor';
import { IssueForm } from '../components/issues/IssueForm';
import { IssueListHeader } from '../components/issues/IssueListHeader';
import { IssueFilters } from '../components/issues/IssueFilters';
import { IssueCard } from '../components/issues/IssueCard';
import { LinkDialog } from '../components/issues/LinkDialog';
import { JiraImportDialog } from '../components/issues/JiraImportDialog';
import { LinearImportDialog } from '../components/issues/LinearImportDialog';
import { ActiveProductIndicator } from '../components/common/ActiveProductIndicator';
import { useApp } from '../contexts/AppContext';
import { getRepoContext } from '../utils/repoContext';
import { useToast } from '../hooks/use-toast';
import { 
  getIssuesMarkdown, 
  updateIssueStatus, 
  updateIssue, 
  deleteIssue,
  findFile,
  type IssueRepoContext 
} from '../lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { listAgentExecutions } from '../lib/api/agent-executions';

export function Issues() {
  const { codingAgent, isConfigured: isJiraConfigured, isLinearConfigured, activeProduct, activeRepoUrl, isLoadingProduct, quickRequestState, setQuickRequest } = useApp();
  const location = useLocation();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [formData, setFormData] = useState<Partial<Issue>>({
    type: 'Bug',
    status: 'Not Started',
    description: '',
    title: '',
    assignedTo: '',
    estimate: 'M',
    effort: 0,
    links: [],
    plan: '',
    labels: [],
    version: ''
  });

  const [skillShopService] = useState(() => new SkillShopService());

  const repoContext = useMemo(
    () => getRepoContext(activeProduct ?? null, activeRepoUrl),
    [activeProduct, activeRepoUrl],
  );

  // Convert repoContext to IssueRepoContext for API calls
  const issueRepoContext: IssueRepoContext = useMemo(() => ({
    repoUrl: repoContext.repoUrl,
    repoPath: repoContext.repoPath,
  }), [repoContext]);

  // Use the generic request processor hook with external state for persistence across navigation
  const requestProcessor = useRequestProcessor({
    onProcess: async (request: string) => {
      const result = await skillShopService.processIssueRequest(request, activeProduct);
      if (result.success && result.result) {
        requestProcessor.setResponse(result.result);
        await loadIssues();
      } else {
        throw new Error(result.error || 'Failed to process issue request');
      }
    },
    onSuccess: () => {
      console.log('Issue request processed successfully');
    },
    externalValue: quickRequestState.issues,
    onExternalValueChange: (value) => setQuickRequest('issues', value),
  });

  // Issue grooming state
  const [groomingIssues, setGroomingIssues] = useState<Set<string>>(new Set());

  // Expanded issue state
  const [expandedIssues, setExpandedIssues] = useState<Set<string>>(new Set());

  // Archive state
  const [isArchiving, setIsArchiving] = useState(false);

  // Code generation state
  const [generatingCodeIssues, setGeneratingCodeIssues] = useState<Set<string>>(new Set());

  // Status editing state
  const [updatingStatusIssues, setUpdatingStatusIssues] = useState<Set<string>>(new Set());

  // Field editing state
  const [editingIssues, setEditingIssues] = useState<Set<string>>(new Set());
  const [editedFields, setEditedFields] = useState<Record<string, Partial<Issue>>>({});

  // Link dialog state
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [currentLinkIssueId, setCurrentLinkIssueId] = useState<string | null>(null);
  const [linkInputValue, setLinkInputValue] = useState('');

  // JIRA import dialog state
  const [showJiraImportDialog, setShowJiraImportDialog] = useState(false);
  const [showLinearImportDialog, setShowLinearImportDialog] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);

  const [showDeleteIssueDialog, setShowDeleteIssueDialog] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState<Issue | null>(null);

  const importOptions = useMemo(() => {
    const options: Array<{ id: 'jira' | 'linear'; label: string }> = [];
    if (isJiraConfigured) {
      options.push({ id: 'jira', label: 'JIRA' });
    }
    if (isLinearConfigured) {
      options.push({ id: 'linear', label: 'Linear' });
    }
    return options;
  }, [isJiraConfigured, isLinearConfigured]);
  const { toast } = useToast();

  const handleImportSelection = (source: 'jira' | 'linear') => {
    setShowImportMenu(false);
    if (source === 'jira') {
      setShowJiraImportDialog(true);
    } else {
      setShowLinearImportDialog(true);
    }
  };

  const handleImportButtonClick = () => {
    if (importOptions.length === 0) {
      return;
    }

    if (importOptions.length === 1) {
      handleImportSelection(importOptions[0].id);
    } else {
      setShowImportMenu(prev => !prev);
    }
  };

  useEffect(() => {
    if (!showImportMenu) {
      return;
    }

    const handleClickAway = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.import-menu')) {
        setShowImportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
    };
  }, [showImportMenu]);

  // Search and filter state (persisted in user settings so filters survive navigation)
  const issueTypes: IssueType[] = ['Bug', 'Task', 'Feature', 'Release'];
  const statusOptions: IssueStatus[] = ['Not Started', 'In Progress', 'Ready For Review', 'In Review', 'Completed', 'Blocked', 'On Hold', 'Groomed'];
  const estimateOptions: EstimateSize[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [estimateFilter, setEstimateFilter] = useState<string>('all');
  const [excludeOnHold, setExcludeOnHold] = useState(false);

  const filtersLoadedRef = useRef(false);

  // Load filter state from user settings on mount (same pattern as Changes page velocitySettings)
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const userSettings = await skillShopService.getUserSettings();
        const saved = userSettings?.integrationSettings?.productApp?.issuesPageFilters;
        if (!saved) {
          filtersLoadedRef.current = true;
          return;
        }
        if (typeof saved.searchTerm === 'string') setSearchTerm(saved.searchTerm);
        if (typeof saved.statusFilter === 'string' && (saved.statusFilter === 'all' || statusOptions.includes(saved.statusFilter as IssueStatus))) {
          setStatusFilter(saved.statusFilter);
        }
        if (typeof saved.typeFilter === 'string' && (saved.typeFilter === 'all' || issueTypes.includes(saved.typeFilter as IssueType))) {
          setTypeFilter(saved.typeFilter);
        }
        if (typeof saved.estimateFilter === 'string' && (saved.estimateFilter === 'all' || estimateOptions.includes(saved.estimateFilter as EstimateSize))) {
          setEstimateFilter(saved.estimateFilter);
        }
        if (typeof saved.excludeOnHold === 'boolean') setExcludeOnHold(saved.excludeOnHold);
      } catch (error) {
        console.error('[Issues] Failed to load filter settings:', error);
      } finally {
        filtersLoadedRef.current = true;
      }
    };
    void loadFilters();
  }, [skillShopService]);

  // Persist filter state to user settings when filters change (after initial load)
  useEffect(() => {
    if (!filtersLoadedRef.current) return;
    skillShopService.updateIssuesPageFilters({
      searchTerm,
      statusFilter,
      typeFilter,
      estimateFilter,
      excludeOnHold,
    }).catch((error) => {
      console.error('[Issues] Failed to save filter settings:', error);
    });
  }, [searchTerm, statusFilter, typeFilter, estimateFilter, excludeOnHold, skillShopService]);

  // Load issues from markdown file
  const { request: requestValue, setRequest, isProcessing, lastResponse, setResponse, isListening, speechSupported, handleProcess, handleKeyPress, toggleListening } = requestProcessor;

  // Sync Groom / Fix it-Build it button state from active agent executions (e.g. after navigation)
  const syncActiveExecutionsForIssues = useCallback(async () => {
    const res = await listAgentExecutions({ status: 'pending,running', limit: 100 });
    if (!res.success || !res.executions) return;
    const issueIds = new Set(issues.map((i) => i.id));
    const grooming = new Set<string>();
    const codeGen = new Set<string>();
    for (const exec of res.executions) {
      const meta = exec.metadata && typeof exec.metadata === 'object' ? exec.metadata as Record<string, unknown> : null;
      const entityId = meta?.issueId ?? meta?.entityId;
      const id = typeof entityId === 'string' ? entityId : null;
      if (!id || !issueIds.has(id)) continue;
      const name = (exec.agentName ?? '').toLowerCase();
      if (name.includes('velocity_grooming')) grooming.add(id);
      else if (name.includes('velocity_code_gen')) codeGen.add(id);
    }
    setGroomingIssues(grooming);
    setGeneratingCodeIssues(codeGen);
  }, [issues]);

  useEffect(() => {
    if (issues.length === 0) return;
    syncActiveExecutionsForIssues();
  }, [issues.length, syncActiveExecutionsForIssues]);

  // Poll active executions while any issue has grooming or code gen in progress
  const hasActiveIssueRuns = groomingIssues.size + generatingCodeIssues.size > 0;
  useEffect(() => {
    if (!hasActiveIssueRuns) return;
    const interval = setInterval(syncActiveExecutionsForIssues, 4000);
    return () => clearInterval(interval);
  }, [hasActiveIssueRuns, syncActiveExecutionsForIssues]);

  const loadIssues = useCallback(async () => {
    setLoadingIssues(true);
    try {
      const result = await getIssuesMarkdown(issueRepoContext);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch issues');
      }
      
      const markdownContent = result.content || '';
      const parsedIssues = parseIssuesFromMarkdown(markdownContent);
      setIssues(parsedIssues);
      
      console.log(`Loaded ${parsedIssues.length} issues from markdown file`);
      
      // Clear any previous error messages
      if (lastResponse.startsWith('Error loading issues:')) {
        setResponse('');
      }
    } catch (error) {
      console.error('Error loading issues:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setResponse(`Error loading issues: ${errorMessage}. Make sure the product server is running on port 3002.`);
    } finally {
      setLoadingIssues(false);
    }
  }, [issueRepoContext, lastResponse, setResponse]);

  // Load issues when repo context is ready
  // Only load if we have an active product - don't load default SkillShop data
  useEffect(() => {
    // Skip if a product is currently being loaded
    if (isLoadingProduct) {
      console.log('[Issues] Waiting for active product to load before fetching issues');
      return;
    }
    
    // Skip if there's no active product - don't load default data
    if (!activeProduct) {
      console.log('[Issues] No active product - skipping data load');
      setIssues([]);
      return;
    }
    
    void loadIssues();
  }, [loadIssues, isLoadingProduct, activeProduct]);

  // Handle scrolling to issue when hash is present in URL
  useEffect(() => {
    if (!location.hash || loadingIssues || issues.length === 0) {
      return;
    }

    // Extract the issue ID from the hash (e.g., "#BUG-001" -> "BUG-001")
    const targetIssueId = location.hash.substring(1);
    
    // Find the issue in the list
    const targetIssue = issues.find(issue => {
      const issueId = issue.id.split(':')[0].trim();
      return issueId === targetIssueId;
    });

    if (targetIssue) {
      // Expand the issue
      setExpandedIssues(prev => new Set(prev).add(targetIssue.id));

      // Scroll to the issue after a short delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(targetIssueId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a highlight effect
          element.style.transition = 'background-color 0.5s';
          element.style.backgroundColor = '#fef3c7';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 300);
    }
  }, [location.hash, loadingIssues, issues]);

  // Generate next issue ID based on type
  const generateIssueId = (type: IssueType): string => {
    const typeCode = type.toUpperCase();
    const existingIssues = issues.filter(issue => issue.id.startsWith(typeCode));
    const nextNumber = existingIssues.length + 1;
    return `${typeCode}-${nextNumber.toString().padStart(3, '0')}`;
  };

  // Handle issue grooming
  const handleGroomIssue = async (issue: Issue) => {
    // Add to grooming set to show loading state
    setGroomingIssues(prev => new Set(prev).add(issue.id));
    
    try {
      const result = await skillShopService.groomIssue(issue, activeProduct);
      
      if (result.success && result.result) {
        console.log('Issue grooming completed:', result.result);
        
        // Reload issues to see any updates made by the grooming process
        await loadIssues();
        
        // Show success message
        toast({
          title: 'Issue groomed',
          description: 'The issue has been updated with grooming results.',
        });
      } else {
        console.error('Failed to groom issue:', result.error);
        toast({
          title: 'Failed to groom issue',
          description: result.error || 'The issue could not be groomed.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error grooming issue:', error);
      toast({
        title: 'Failed to groom issue',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      // Remove from grooming set
      setGroomingIssues(prev => {
        const newSet = new Set(prev);
        newSet.delete(issue.id);
        return newSet;
      });
    }
  };

  // Handle archive completed issues
  const handleArchiveCompleted = async () => {
    setIsArchiving(true);
    
    try {
      const result = await skillShopService.archiveCompletedIssues(repoContext.repoUrl, repoContext.repoPath);
      
      if (result.success) {
        console.log('Issues archived successfully:', result.result);
        
        // Show success message with count
        const message = result.archivedCount === 0 
          ? 'No completed issues found to archive.'
          : `Successfully archived ${result.archivedCount} completed issue${result.archivedCount === 1 ? '' : 's'}.`;
        
        toast({
          title: 'Archive completed',
          description: message,
        });
        
        // Reload issues to see the updated list
        await loadIssues();
      } else {
        console.error('Failed to archive issues:', result.error);
        toast({
          title: 'Failed to archive issues',
          description: result.error || 'Completed issues could not be archived.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error archiving issues:', error);
      toast({
        title: 'Failed to archive issues',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRequestDeleteIssue = (issue: Issue) => {
    setIssueToDelete(issue);
    setShowDeleteIssueDialog(true);
  };

  const confirmDeleteIssue = async () => {
    if (!issueToDelete) return;

    const issueCode = issueToDelete.id.split(':')[0].trim();
    const productId = activeProduct?.id || 'default';

    try {
      const result = await deleteIssue(productId, issueCode, issueRepoContext);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete issue');
      }

      setExpandedIssues((prev) => {
        const next = new Set(prev);
        next.delete(issueToDelete.id);
        return next;
      });
      setShowDeleteIssueDialog(false);
      setIssueToDelete(null);

      await loadIssues();

      toast({
        title: 'Issue deleted',
        description: `${issueCode} was removed from issues.md.`,
      });
    } catch (error) {
      console.error('Error deleting issue:', error);
      toast({
        title: 'Failed to delete issue',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle code generation for issues
  const handleGenerateCode = async (issue: Issue) => {
    // Add to generating set to show loading state
    setGeneratingCodeIssues(prev => new Set(prev).add(issue.id));
    
    try {
      const result = await skillShopService.generateCodeForIssue(issue, codingAgent, activeProduct);
      
      if (result.success && result.result) {
        console.log('Code generation completed:', result.result);
        
        // Reload issues to show updated code log
        await loadIssues();
        
        // Show success message with generated code
        toast({
          title: 'Code generated',
          description: `Code was generated successfully for ${issue.id}. Check the issue details for the log.`,
        });
      } else {
        console.error('Failed to generate code:', result.error);
        toast({
          title: 'Failed to generate code',
          description: result.error || 'The code generation request failed.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error generating code:', error);
      toast({
        title: 'Failed to generate code',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      // Remove from generating set
      setGeneratingCodeIssues(prev => {
        const newSet = new Set(prev);
        newSet.delete(issue.id);
        return newSet;
      });
    }
  };

  // Handle status update for issues
  const handleStatusUpdate = async (issue: Issue, newStatus: IssueStatus) => {
    if (issue.status === newStatus) {
      return; // No change needed
    }

    // Add to updating set to show loading state
    setUpdatingStatusIssues(prev => new Set(prev).add(issue.id));
    
    try {
      // Extract the issue code (e.g., "BUG-005" from "BUG-005: Title")
      const issueCode = issue.id.split(':')[0].trim();
      
      // Get product UUID from activeProduct (API expects id, not display productId)
      const productId = activeProduct?.id || 'default';
      
      const result = await updateIssueStatus(productId, issueCode, newStatus, issueRepoContext);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update status');
      }
      
      console.log('Status update completed:', result);
      
      // Update local state directly to avoid scroll jump from full reload
      setIssues(prev => prev.map(i => 
        i.id === issue.id 
          ? { ...i, status: newStatus, updatedAt: new Date().toISOString() }
          : i
      ));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error updating status:', error);
      toast({
        title: 'Failed to update status',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      // Remove from updating set
      setUpdatingStatusIssues(prev => {
        const newSet = new Set(prev);
        newSet.delete(issue.id);
        return newSet;
      });
    }
  };

  // Get the appropriate code action label based on issue type
  const getCodeActionLabel = (issueType: IssueType): string => {
    switch (issueType) {
      case 'Bug': return 'Fix It';
      case 'Task': return 'Code It';
      case 'Feature': return 'Build It';
      case 'Release': return 'Prepare It';
      default: return 'Code It';
    }
  };

  // Check if an issue can have code generated (must be groomed)
  const canGenerateCode = (issue: Issue): boolean => {
    return issue.status === 'Groomed';
  };

  // Toggle expanded view for an issue
  const toggleIssueExpansion = (issueId: string) => {
    setExpandedIssues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(issueId)) {
        newSet.delete(issueId);
      } else {
        newSet.add(issueId);
      }
      return newSet;
    });
  };

  // Start editing an issue
  const handleStartEditing = (issue: Issue) => {
    setEditingIssues(prev => new Set(prev).add(issue.id));
    setEditedFields(prev => ({
      ...prev,
      [issue.id]: {
        description: issue.description,
        plan: issue.plan || '',
        links: issue.links || []
      }
    }));
  };

  // Cancel editing an issue
  const handleCancelEditing = (issueId: string) => {
    setEditingIssues(prev => {
      const newSet = new Set(prev);
      newSet.delete(issueId);
      return newSet;
    });
    setEditedFields(prev => {
      const newFields = { ...prev };
      delete newFields[issueId];
      return newFields;
    });
  };

  // Update edited field value
  const handleFieldChange = (issueId: string, field: keyof Issue, value: any) => {
    setEditedFields(prev => ({
      ...prev,
      [issueId]: {
        ...prev[issueId],
        [field]: value
      }
    }));
  };

  // Add a new link
  const handleAddLink = (issueId: string) => {
    setCurrentLinkIssueId(issueId);
    setLinkInputValue('');
    setShowLinkDialog(true);
  };

  // Handle link input from text field
  const handleAddLinkFromInput = () => {
    if (currentLinkIssueId && linkInputValue.trim()) {
      const currentLinks = editedFields[currentLinkIssueId]?.links || [];
      handleFieldChange(currentLinkIssueId, 'links', [...currentLinks, linkInputValue.trim()]);
      setShowLinkDialog(false);
      setLinkInputValue('');
      setCurrentLinkIssueId(null);
    }
  };

  // Handle link input from file browser
  const handleAddLinkFromFile = async () => {
    if (!currentLinkIssueId) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = false;
    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (file) {
        const fileName = file.name;
        
        // Try to find the file in the codebase to get the correct relative path
        try {
          const result = await findFile(fileName);
          
          let filePath = fileName; // Default to just filename if search fails
          
          if (result.success && result.found && result.path) {
            // Use the found path relative to project root
            filePath = result.path;
            console.log(`Found file in codebase: ${filePath}`);
          } else {
            console.log(`File not found in codebase, using filename only: ${fileName}`);
          }
          
          const currentLinks = editedFields[currentLinkIssueId]?.links || [];
          handleFieldChange(currentLinkIssueId, 'links', [...currentLinks, filePath]);
          setShowLinkDialog(false);
          setLinkInputValue('');
          setCurrentLinkIssueId(null);
        } catch (error) {
          console.error('Error searching for file:', error);
          // Fallback to just the filename
          const currentLinks = editedFields[currentLinkIssueId]?.links || [];
          handleFieldChange(currentLinkIssueId, 'links', [...currentLinks, fileName]);
          setShowLinkDialog(false);
          setLinkInputValue('');
          setCurrentLinkIssueId(null);
        }
      }
    };
    input.click();
  };

  // Cancel link dialog
  const handleCancelLinkDialog = () => {
    setShowLinkDialog(false);
    setLinkInputValue('');
    setCurrentLinkIssueId(null);
  };

  // Delete a link
  const handleDeleteLink = (issueId: string, linkIndex: number) => {
    const currentLinks = editedFields[issueId]?.links || [];
    const newLinks = currentLinks.filter((_, index) => index !== linkIndex);
    handleFieldChange(issueId, 'links', newLinks);
  };

  // Save edited fields
  const handleSaveEdits = async (issue: Issue) => {
    const edits = editedFields[issue.id];
    if (!edits) return;

    // Add to updating set to show loading state
    setUpdatingStatusIssues(prev => new Set(prev).add(issue.id));
    
    try {
      // Extract the issue ID (e.g., "BUG-005" from "BUG-005: Title")
      const issueId = issue.id.split(':')[0];
      const productId = activeProduct?.id || 'default';
      
      const result = await updateIssue(
        productId,
        issueId, 
        {
          description: edits.description,
          plan: edits.plan,
          links: edits.links
        },
        issueRepoContext
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update issue');
      }
      
      console.log('Field updates completed:', result);
      
      // Clear editing state
      handleCancelEditing(issue.id);
      
      // Reload issues to see the updated fields
      await loadIssues();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error updating issue:', error);
      toast({
        title: 'Failed to update issue',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      // Remove from updating set
      setUpdatingStatusIssues(prev => {
        const newSet = new Set(prev);
        newSet.delete(issue.id);
        return newSet;
      });
    }
  };

  const handleInputChange = (field: keyof Issue, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (field: 'links' | 'labels', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    setFormData(prev => ({
      ...prev,
      [field]: items
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      toast({
        title: 'Missing required fields',
        description: 'Both title and description are required to create an issue.',
        variant: 'destructive',
      });
      return;
    }

    const issueId = generateIssueId(formData.type as IssueType);
    const now = new Date().toISOString();
    
    const newIssue: Issue = {
      id: `${issueId}: ${formData.title}`,
      type: formData.type as IssueType,
      title: formData.title as string,
      description: formData.description as string,
      createdAt: now,
      updatedAt: now,
      status: formData.status as IssueStatus,
      assignedTo: formData.assignedTo || undefined,
      estimate: formData.estimate as EstimateSize,
      effort: formData.effort || 0,
      links: formData.links || [],
      plan: formData.plan || undefined,
      labels: formData.labels || [],
      version: formData.version || undefined
    };

    setIssues(prev => [...prev, newIssue]);
    
    // Reset form
    setFormData({
      type: 'Bug',
      status: 'Not Started',
      description: '',
      title: '',
      assignedTo: '',
      estimate: 'M',
      effort: 0,
      links: [],
      plan: '',
      labels: [],
      version: ''
    });
    setShowForm(false);
    
    // TODO: Save to data/product/issues.md file
    console.log('New issue created:', newIssue);
  };

  // Filter issues based on search term and filter criteria
  const filteredIssues = issues.filter(issue => {
    // Search across title, description, labels, and assignee
    const matchesSearch = searchTerm === '' ||
      issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.labels && issue.labels.some(label => label.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      (issue.assignedTo && issue.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filter by status
    const matchesStatus = statusFilter === 'all' || issue.status === statusFilter;
    const notExcludedOnHold = !excludeOnHold || issue.status !== 'On Hold';

    // Filter by type
    const matchesType = typeFilter === 'all' || issue.type === typeFilter;

    // Filter by estimate
    const matchesEstimate = estimateFilter === 'all' || issue.estimate === estimateFilter;

    return matchesSearch && matchesStatus && notExcludedOnHold && matchesType && matchesEstimate;
  });

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setEstimateFilter('all');
    setExcludeOnHold(false);
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || typeFilter !== 'all' || estimateFilter !== 'all' || excludeOnHold;

  const getStatusColor = (status: IssueStatus) => {
    switch (status) {
      case 'Not Started': return '#6c757d';
      case 'In Progress': return '#007bff';
      case 'Ready For Review': return '#17a2b8';
      case 'In Review': return '#8b5cf6';
      case 'Completed': return '#28a745';
      case 'Blocked': return '#dc3545';
      case 'On Hold': return '#ffc107';
      case 'Groomed': return '#6f42c1';
      default: return '#6c757d';
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Bug style={{ marginRight: '12px', color: '#0066cc' }} />
              <h2 style={{ margin: 0 }}>Issue Management</h2>
            </div>
            <div style={{ height: '24px', width: '1px', backgroundColor: '#e5e7eb' }} />
            <ActiveProductIndicator />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div className="import-menu" style={{ position: 'relative' }}>
              <button
                className="btn-compact"
                onClick={handleImportButtonClick}
                style={{ backgroundColor: '#6366f1' }}
                disabled={importOptions.length === 0}
                title={importOptions.length === 0 ? 'Configure an external integration in Settings to enable imports' : 'Import issues from a connected integration'}
              >
                <Download size={14} />
                Import
              </button>
              {showImportMenu && importOptions.length > 1 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    padding: '8px 0',
                    minWidth: '180px',
                    zIndex: 1000
                  }}
                >
                  {importOptions.map(option => (
                    <button
                      key={option.id}
                      onClick={() => handleImportSelection(option.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 16px',
                        background: 'transparent',
                        border: 'none',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="btn-compact"
              onClick={() => setShowForm(!showForm)}
            >
              <Plus size={14} />
              {showForm ? 'Cancel' : 'New Issue'}
            </button>
          </div>
        </div>

        {repoContext.repoUrl && (
          <div style={{ marginBottom: '16px', fontSize: '12px', color: '#2563eb' }}>
            Managing issues for <span style={{ fontFamily: 'monospace' }}>{repoContext.repoUrl}</span>
            {repoContext.repoPath && (
              <span style={{ color: '#475569' }}> ({repoContext.repoPath})</span>
            )}
          </div>
        )}

        <RequestInput
          title="Quick Issue Request"
          description="Describe your issue request in natural language."
          examples={[
            "Create a bug for login not working",
            "Update ISSUE-001 to completed",
            "Show me all open bugs",
            "Create a feature for dark mode"
          ]}
          value={requestValue}
          isProcessing={isProcessing}
          isListening={isListening}
          lastResponse={lastResponse}
          speechSupported={speechSupported}
          onValueChange={setRequest}
          onProcess={handleProcess}
          onVoiceInput={toggleListening}
          onKeyPress={handleKeyPress}
          data-walkthrough="quick-request"
        />

        {showForm && (
          <IssueForm
            formData={formData}
            issueTypes={issueTypes}
            statusOptions={statusOptions}
            estimateOptions={estimateOptions}
            onInputChange={handleInputChange}
            onArrayInputChange={handleArrayInputChange}
            onSubmit={handleSubmit}
            onCancel={() => setShowForm(false)}
          />
        )}

        <div>
          <IssueListHeader
            filteredCount={filteredIssues.length}
            totalCount={issues.length}
            isArchiving={isArchiving}
            isLoading={loadingIssues}
            onArchiveCompleted={handleArchiveCompleted}
            onRefresh={loadIssues}
          />

          <IssueFilters
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            typeFilter={typeFilter}
            estimateFilter={estimateFilter}
            excludeOnHold={excludeOnHold}
            onSearchChange={setSearchTerm}
            onStatusFilterChange={setStatusFilter}
            onTypeFilterChange={setTypeFilter}
            onEstimateFilterChange={setEstimateFilter}
            onExcludeOnHoldChange={setExcludeOnHold}
            onClearFilters={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
            issueTypes={issueTypes}
            statusOptions={statusOptions}
            estimateOptions={estimateOptions}
          />

          {loadingIssues ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <Loader size={48} style={{ marginBottom: '16px', opacity: 0.5, animation: 'spin 1s linear infinite' }} />
              <p>Loading issues from data/product/issues.md...</p>
            </div>
          ) : issues.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <Bug size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No issues yet{repoContext.repoUrl ? ' for this product' : ''}.</p>
              <p style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
                Create your first issue to get started, or import issues from an external system.
              </p>
            </div>
          ) : filteredIssues.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <Search size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No issues match your search and filter criteria.</p>
              <button
                onClick={handleClearFilters}
                className="btn"
                style={{
                  marginTop: '16px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FilterX size={16} />
                Clear All Filters
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredIssues.map((issue, index) => (
                <IssueCard
                  key={index}
                  issue={issue}
                  isExpanded={expandedIssues.has(issue.id)}
                  isGrooming={groomingIssues.has(issue.id)}
                  isGeneratingCode={generatingCodeIssues.has(issue.id)}
                  isEditing={editingIssues.has(issue.id)}
                  isUpdating={updatingStatusIssues.has(issue.id)}
                  editedFields={editedFields[issue.id] || {}}
                  statusOptions={statusOptions}
                  canGenerateCode={canGenerateCode(issue)}
                  getCodeActionLabel={getCodeActionLabel}
                  getStatusColor={getStatusColor}
                  onToggleExpansion={() => toggleIssueExpansion(issue.id)}
                  onGroomIssue={() => handleGroomIssue(issue)}
                  onGenerateCode={() => handleGenerateCode(issue)}
                  onStartEditing={() => handleStartEditing(issue)}
                  onCancelEditing={() => handleCancelEditing(issue.id)}
                  onSaveEdits={() => handleSaveEdits(issue)}
                  onFieldChange={(field, value) => handleFieldChange(issue.id, field, value)}
                  onStatusUpdate={(newStatus) => handleStatusUpdate(issue, newStatus)}
                  onAddLink={() => handleAddLink(issue.id)}
                  onDeleteLink={(linkIndex) => handleDeleteLink(issue.id, linkIndex)}
                  onRequestDelete={() => handleRequestDeleteIssue(issue)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <LinkDialog
        show={showLinkDialog}
        linkInputValue={linkInputValue}
        onLinkInputChange={setLinkInputValue}
        onAddFromInput={handleAddLinkFromInput}
        onAddFromFile={handleAddLinkFromFile}
        onCancel={handleCancelLinkDialog}
      />

      {showJiraImportDialog && (
        <JiraImportDialog
          onClose={() => setShowJiraImportDialog(false)}
          onImportComplete={() => {
            loadIssues();
          }}
        />
      )}

      {showLinearImportDialog && (
        <LinearImportDialog
          onClose={() => setShowLinearImportDialog(false)}
          onImportComplete={() => {
            loadIssues();
          }}
        />
      )}

      <AlertDialog
        open={showDeleteIssueDialog}
        onOpenChange={(open) => {
          setShowDeleteIssueDialog(open);
          if (!open) setIssueToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Issue</AlertDialogTitle>
            <AlertDialogDescription>
              {issueToDelete
                ? `Are you sure you want to delete ${issueToDelete.id.split(':')[0].trim()}? This removes it from issues.md. You can revert in Changes.`
                : 'Are you sure you want to delete this issue? You can revert in Changes.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteIssue} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add CSS for the loader animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
} 